import type { Zone, ZoneSummary, ZoneTreeNode } from "@gettactic/api";
import type { IconValue } from "@gettactic/components/src/icons";

export interface FlatZoneRow extends ZoneSummary {
  depth: number;
}

/** Depth-first flattening of the zone tree, parents before children. */
export const flattenZoneTree = (
  nodes: ZoneTreeNode[],
  depth = 0
): FlatZoneRow[] =>
  nodes.flatMap((node) => {
    const { children, ...summary } = node;
    return [
      { ...summary, depth },
      ...flattenZoneTree(children ?? [], depth + 1)
    ];
  });

/** Ids of a zone and everything under it, from a flat list. */
export const zoneSubtreeIds = (
  zoneId: string,
  zones: Pick<Zone, "id" | "parent_id">[]
): Set<string> => {
  const childrenOf = new Map<string, string[]>();
  for (const zone of zones) {
    if (zone.parent_id) {
      childrenOf.set(zone.parent_id, [
        ...(childrenOf.get(zone.parent_id) ?? []),
        zone.id
      ]);
    }
  }
  const ids = new Set<string>([zoneId]);
  const queue = [zoneId];
  while (queue.length) {
    const current = queue.shift() as string;
    for (const child of childrenOf.get(current) ?? []) {
      if (!ids.has(child)) {
        ids.add(child);
        queue.push(child);
      }
    }
  }
  return ids;
};

/** Zones store their icon as the serialised icon-set value. */
export const parseZoneIcon = (
  icon: string | null | undefined
): IconValue | undefined => {
  if (!icon) return undefined;
  try {
    const parsed = JSON.parse(icon) as Partial<IconValue>;
    if (
      parsed &&
      typeof parsed.icon === "string" &&
      typeof parsed.set === "string"
    ) {
      return { icon: parsed.icon, set: parsed.set };
    }
  } catch {
    // A legacy or hand-written value: nothing to render.
  }
  return undefined;
};

export const serializeZoneIcon = (
  icon: IconValue | undefined | null
): string | null => (icon ? JSON.stringify(icon) : null);

export const formatSqm = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : `${value.toFixed(1)} m²`;

export const formatSqmPerDesk = (
  areaSqm: number | null | undefined,
  desks: number
): string =>
  areaSqm === null || areaSqm === undefined || desks === 0
    ? "—"
    : `${(areaSqm / desks).toFixed(1)} m²`;
